/**
 * AI Hub API Setup
 * Uses shared @quotech/api bootstrap — see packages/api/src/setupHubApi.ts
 */
import { setupHubApi } from '@quotech/api'
import { getDefaultClient } from '@quotech/api'

/** Call once in main.tsx before rendering. */
export function setupAIApi(): void {
  setupHubApi({ hubName: 'AI' })
}

// Re-export configured client
export const apiClient = getDefaultClient()

/**
 * AI-specific API endpoints based on quotech-ai backend
 */
export const aiApi = {
  // Document Extraction
  extractFromDocument: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return getDefaultClient().post('/api/ai/extraction/document/', formData)
  },
  extractFromImage: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return getDefaultClient().post('/api/ai/extraction/image/', formData)
  },
  extractFromPdf: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return getDefaultClient().post('/api/ai/extraction/pdf/', formData)
  },

  // Similarity Search
  findSimilarJobs: (description: string, limit?: number) =>
    getDefaultClient().post('/api/ai/similarity/jobs/', { description, limit: limit || 10 }),
  findSimilarQuotes: (description: string, limit?: number) =>
    getDefaultClient().post('/api/ai/similarity/quotes/', { description, limit: limit || 10 }),
  findSimilarItems: (description: string, limit?: number) =>
    getDefaultClient().post('/api/ai/similarity/items/', { description, limit: limit || 10 }),

  // Smart Suggestions
  getSuggestions: (context: { type: string; input: string; metadata?: Record<string, unknown> }) =>
    getDefaultClient().post('/api/ai/suggestions/', context),
  getQuoteItemSuggestions: (description: string) =>
    getDefaultClient().post('/api/ai/suggestions/quote-items/', { description }),
  getCategorySuggestions: (text: string) =>
    getDefaultClient().post('/api/ai/suggestions/categories/', { text }),

  // Calculations
  calculateEstimate: (data: { scope: string; items?: unknown[]; historical_data?: boolean }) =>
    getDefaultClient().post('/api/ai/calculations/estimate/', data),
  optimizePricing: (data: { items: unknown[]; target_margin?: number; constraints?: unknown }) =>
    getDefaultClient().post('/api/ai/calculations/optimize/', data),

  // Chat/Assistant
  chat: (messages: Array<{ role: string; content: string }>, context?: Record<string, unknown>) =>
    getDefaultClient().post('/api/ai/chat/', { messages, context }),
  streamChat: (messages: Array<{ role: string; content: string }>, context?: Record<string, unknown>) =>
    getDefaultClient().post('/api/ai/chat/stream/', { messages, context }, { responseType: 'stream' }),

  // Analytics
  getUsageStats: () => getDefaultClient().get('/api/ai/stats/usage/'),
  getModelPerformance: () => getDefaultClient().get('/api/ai/stats/performance/'),
}
